/**
 * AEOS control-plane change requests: review, approval and creation of intent.
 *
 * Three constraints drive this file.
 *
 * 1. Approval is the server's decision. The button is driven only by the
 *    `approval.allowed` flag returned with the record, and `approval.reason` is shown
 *    beside the disabled button. Group membership and the self-approval rule are not
 *    re-implemented here; a browser that decides its own permissions is decoration.
 * 2. The bytes are shown, not summarized. Attesting to a SHA-256 you cannot read is
 *    theatre, so `canonicalJson` is rendered verbatim in a focusable, wrapping block.
 * 3. Nothing is applied. Every response asserts `applied: false`, and that is surfaced
 *    on creation, on approval and in the review panel.
 */
import { useCallback, useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Modal,
  Multiselect,
  RadioGroup,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
  Textarea,
  type SelectProps,
} from '@cloudscape-design/components';

import { AeosLimitationsNotice, AeosScopeBadge } from './AeosEstatePanels';
import {
  AEOS_BILLABLE_PHASES,
  AEOS_CHANGE_KINDS,
  AEOS_INVOICE_POLICY_BOUNDS,
  AEOS_REASON_MAX_LENGTH,
  aeosChangeRequestCreateSchema,
  approveAeosChange,
  createAeosChange,
  getAeosChange,
  isAeosRevisionConflict,
  listAeosChanges,
  type AeosApprovalReason,
  type AeosBillablePhase,
  type AeosChangeKind,
  type AeosChangeRequestCreate,
  type AeosChangeReview,
  type AeosChangeSummary,
  type AeosDesiredChange,
} from '../../api/aeos';

const CANONICAL_JSON_STYLE: CSSProperties = {
  margin: 0,
  padding: '0.75rem',
  maxHeight: '22rem',
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  fontFamily: 'monospace',
  fontSize: '0.85rem',
  border: '1px solid currentColor',
  borderRadius: '0.25rem',
};

const APPLIED_NOTICE =
  'Approval records reviewed intent and provisions nothing. Every response from this API asserts applied: false — no applier exists, and Peregrine never writes AEOS.';

const CHANGE_SURFACE_REPRESENTED = [
  'the desired state an operator signed, its canonical bytes and their SHA-256 digest',
  'content revision (moves with the desired state) and lifecycle revision (moves on approval)',
  'who created the request and, once approved, who approved it and when',
  'whether the server will accept an approval from you, and why not if it will not',
] as const;

const CHANGE_SURFACE_NOT_REPRESENTED = [
  'nothing here has been applied to AEOS: approval is intent, and no applier consumes it yet',
  'the list view returns summaries only — canonical bytes are fetched per record for review',
  'current live AEOS values are not shown beside a proposed change; compare against the Control plane tab',
  'the change API returns no coverage block, so these limitations are stated by this console rather than by the server',
] as const;

interface AeosChangeViewProps {
  /** Bumped by the page refresh so the list reloads with the rest of the surface. */
  reloadToken: number;
}

export function AeosChangeView({ reloadToken }: AeosChangeViewProps): ReactElement {
  const [summaries, setSummaries] = useState<AeosChangeSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [review, setReview] = useState<AeosChangeReview | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isApprovalVisible, setIsApprovalVisible] = useState(false);
  const [isApprovalConfirmed, setIsApprovalConfirmed] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();

  const loadList = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await listAeosChanges(50);
      setSummaries(response.changeRequests);
    } catch (loadError) {
      setError(
        loadError instanceof Error ? loadError.message : 'Unable to list change requests',
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList, reloadToken]);

  useEffect(() => {
    if (selectedId === undefined) {
      setReview(undefined);
      return;
    }
    let isCancelled = false;
    void getAeosChange(selectedId)
      .then((detail) => {
        if (!isCancelled) setReview(detail);
      })
      .catch((detailError: unknown) => {
        if (!isCancelled) {
          setError(
            detailError instanceof Error
              ? detailError.message
              : 'Unable to read the change request',
          );
        }
      });
    return () => {
      isCancelled = true;
    };
  }, [selectedId, reloadToken]);

  /**
   * Any move in the identity, lifecycle revision or hash invalidates the attestation,
   * so the confirmation is cleared rather than carried over to different bytes.
   */
  useEffect(() => {
    setIsApprovalConfirmed(false);
  }, [review?.changeRequestId, review?.lifecycleRevision, review?.desiredStateHash]);

  const reloadDetail = useCallback(async (changeRequestId: string): Promise<void> => {
    try {
      setReview(await getAeosChange(changeRequestId));
    } catch (detailError) {
      setError(
        detailError instanceof Error
          ? detailError.message
          : 'Unable to reload the change request',
      );
    }
  }, []);

  async function submitCreate(input: AeosChangeRequestCreate): Promise<void> {
    setIsCreating(true);
    setError(undefined);
    try {
      const result = await createAeosChange(input);
      setIsFormVisible(false);
      setReview(result.changeRequest);
      setSelectedId(result.changeRequest.changeRequestId);
      setNotice(
        `${result.idempotentReplay ? 'The existing idempotent request was returned' : 'Change request recorded and awaiting independent approval'}. applied = ${String(result.applied)}: nothing was provisioned in AEOS.`,
      );
      await loadList();
    } catch (createError) {
      setError(
        createError instanceof Error ? createError.message : 'Unable to create the change request',
      );
    } finally {
      setIsCreating(false);
    }
  }

  async function approveSelected(): Promise<void> {
    if (!review || !review.approval.allowed || !isApprovalConfirmed) {
      return;
    }
    const changeRequestId = review.changeRequestId;
    setIsApproving(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const result = await approveAeosChange({
        changeRequestId,
        expectedLifecycleRevision: review.lifecycleRevision,
        expectedDesiredStateHash: review.desiredStateHash,
      });
      setReview(result.changeRequest);
      setIsApprovalVisible(false);
      setIsApprovalConfirmed(false);
      setNotice(
        `Approval recorded${result.idempotentReplay ? ' (idempotent replay of an existing approval)' : ''}. applied = ${String(result.applied)}: nothing was provisioned in AEOS.`,
      );
      await loadList();
    } catch (approvalError) {
      setIsApprovalVisible(false);
      setIsApprovalConfirmed(false);
      setError(approvalFailureMessage(approvalError));
      await reloadDetail(changeRequestId);
      await loadList();
    } finally {
      setIsApproving(false);
    }
  }

  return (
    <SpaceBetween size="l">
      <Alert type="info" header="Approval records intent — it provisions nothing">
        {APPLIED_NOTICE}
      </Alert>
      {error ? (
        <Alert type="error" header="Change-request action failed">
          {error}
        </Alert>
      ) : null}
      {notice ? <Alert type="success">{notice}</Alert> : null}
      <AeosLimitationsNotice
        headerText="Limitations of this view — what an approved change request does and does not do"
        represented={CHANGE_SURFACE_REPRESENTED}
        notRepresented={CHANGE_SURFACE_NOT_REPRESENTED}
        sourceNote="Stated by this console. Unlike the estate and control-plane reads, the change API returns no coverage block."
      />
      <ChangeSummaryTable
        summaries={summaries}
        isLoading={isLoading}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onRefresh={loadList}
        onCreate={() => setIsFormVisible(true)}
      />
      {review ? (
        <ChangeReviewPanel
          review={review}
          onOpenApproval={() => setIsApprovalVisible(true)}
        />
      ) : (
        <Box textAlign="center" padding="l">
          Select a change request to review its canonical desired state.
        </Box>
      )}
      <ChangeFormModal
        isVisible={isFormVisible}
        isSubmitting={isCreating}
        onDismiss={() => setIsFormVisible(false)}
        onSubmit={submitCreate}
      />
      <ApprovalModal
        review={review}
        isVisible={isApprovalVisible}
        isConfirmed={isApprovalConfirmed}
        isSubmitting={isApproving}
        onConfirm={setIsApprovalConfirmed}
        onDismiss={() => {
          setIsApprovalVisible(false);
          setIsApprovalConfirmed(false);
        }}
        onApprove={approveSelected}
      />
    </SpaceBetween>
  );
}

/** The shared client discards the status code, so the conflict is read from the text. */
function approvalFailureMessage(error: unknown): string {
  if (isAeosRevisionConflict(error)) {
    return 'The change request moved while it was open, so the approval was refused. The detail has been reloaded — re-review the current revision and canonical bytes before approving again.';
  }
  return error instanceof Error ? error.message : 'Unable to approve the change request';
}

/* ------------------------------------------------------------------ */
/* Summary list                                                        */
/* ------------------------------------------------------------------ */

interface ChangeSummaryTableProps {
  summaries: AeosChangeSummary[];
  isLoading: boolean;
  selectedId: string | undefined;
  onSelect: (changeRequestId: string | undefined) => void;
  onRefresh: () => Promise<void>;
  onCreate: () => void;
}

function ChangeSummaryTable(props: ChangeSummaryTableProps): ReactElement {
  return (
    <Table
      variant="container"
      loading={props.isLoading}
      loadingText="Listing change requests"
      items={props.summaries}
      trackBy="changeRequestId"
      selectionType="single"
      selectedItems={props.summaries.filter(
        (summary) => summary.changeRequestId === props.selectedId,
      )}
      onSelectionChange={({ detail }) =>
        props.onSelect(detail.selectedItems[0]?.changeRequestId)
      }
      empty={<Box textAlign="center">No AEOS change requests.</Box>}
      header={
        <Header
          variant="h2"
          counter={`(${props.summaries.length})`}
          description="Summaries only. Canonical bytes are read per record below, where they are actually being reviewed."
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                iconName="refresh"
                loading={props.isLoading}
                onClick={() => void props.onRefresh()}
              >
                Refresh
              </Button>
              <Button variant="primary" onClick={props.onCreate}>
                Record a change request
              </Button>
            </SpaceBetween>
          }
        >
          Change requests
        </Header>
      }
      columnDefinitions={[
        { id: 'kind', header: 'Kind', cell: (summary) => summary.kind },
        { id: 'scope', header: 'Scope', cell: (summary) => <AeosScopeBadge scope={summary.scope} /> },
        {
          id: 'status',
          header: 'Status',
          cell: (summary) => (
            <StatusIndicator type={summary.status === 'approved' ? 'success' : 'pending'}>
              {summary.status}
            </StatusIndicator>
          ),
        },
        {
          id: 'applied',
          header: 'Applied to AEOS',
          cell: () => <StatusIndicator type="info">no — intent only</StatusIndicator>,
        },
        {
          id: 'contentRevision',
          header: 'Content revision',
          cell: (summary) => summary.contentRevision,
        },
        {
          id: 'lifecycleRevision',
          header: 'Lifecycle revision',
          cell: (summary) => summary.lifecycleRevision,
        },
        {
          id: 'hash',
          header: 'Desired-state hash',
          cell: (summary) => <code>{summary.desiredStateHash.slice(0, 12)}…</code>,
        },
        { id: 'created', header: 'Created at', cell: (summary) => summary.createdAt },
        { id: 'approved', header: 'Approved at', cell: (summary) => summary.approvedAt ?? '—' },
      ]}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Review panel                                                        */
/* ------------------------------------------------------------------ */

interface ChangeReviewPanelProps {
  review: AeosChangeReview;
  onOpenApproval: () => void;
}

function ChangeReviewPanel({ review, onOpenApproval }: ChangeReviewPanelProps): ReactElement {
  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Review the desired change and the exact canonical bytes. The hash below is a digest of these bytes and of nothing else."
          actions={
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <Box variant="small">{approvalReasonText(review.approval.reason)}</Box>
              <Button
                variant="primary"
                disabled={!review.approval.allowed}
                onClick={onOpenApproval}
              >
                Approve reviewed revision
              </Button>
            </SpaceBetween>
          }
        >
          Change request {review.changeRequestId}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {review.scope === 'global' ? (
          <Alert type="warning" header="Global blast radius">
            This change is scoped to every customer account. AEOS holds a single row for
            module settings and for the invoice policy, so there is no per-customer
            version of this change.
          </Alert>
        ) : null}
        <ReviewFacts review={review} />
        <div>
          <Header variant="h3">Desired change</Header>
          <DesiredChangeDetails change={review.desiredChange} />
        </div>
        <CanonicalJsonBlock canonicalJson={review.canonicalJson} hash={review.desiredStateHash} />
      </SpaceBetween>
    </Container>
  );
}

interface ReviewFactsProps {
  review: AeosChangeReview;
}

function ReviewFacts({ review }: ReviewFactsProps): ReactElement {
  return (
    <ColumnLayout columns={3} variant="text-grid">
      <div>
        <Box variant="awsui-key-label">Kind and scope</Box>
        <Box>{review.kind}</Box>
        <AeosScopeBadge scope={review.scope} />
      </div>
      <div>
        <Box variant="awsui-key-label">Status</Box>
        <StatusIndicator type={review.status === 'approved' ? 'success' : 'pending'}>
          {review.status}
        </StatusIndicator>
      </div>
      <div>
        <Box variant="awsui-key-label">Applied to AEOS</Box>
        <Box>{String(review.applied)} — approval records intent and provisions nothing</Box>
      </div>
      <div>
        <Box variant="awsui-key-label">Content revision</Box>
        <Box>{review.contentRevision}</Box>
        <Box variant="small">moves only when the desired state moves; pinned to the hash</Box>
      </div>
      <div>
        <Box variant="awsui-key-label">Lifecycle revision</Box>
        <Box>{review.lifecycleRevision}</Box>
        <Box variant="small">moves only on a lifecycle transition, so far only approval</Box>
      </div>
      <div>
        <Box variant="awsui-key-label">Canonicalization version</Box>
        <Box>{review.canonicalizationVersion}</Box>
      </div>
      <div>
        <Box variant="awsui-key-label">Created</Box>
        <Box>
          {review.createdBy} at {review.createdAt}
        </Box>
      </div>
      <div>
        <Box variant="awsui-key-label">Approved</Box>
        <Box>
          {review.approvedBy === undefined
            ? 'not approved'
            : `${review.approvedBy} at ${review.approvedAt ?? 'unknown time'}`}
        </Box>
      </div>
      <div>
        <Box variant="awsui-key-label">Updated at</Box>
        <Box>{review.updatedAt}</Box>
      </div>
    </ColumnLayout>
  );
}

interface CanonicalJsonBlockProps {
  canonicalJson: string;
  hash: string;
}

/**
 * Read-only, wrapping and keyboard-focusable.
 *
 * Focusable because a scrollable region an operator cannot reach with a keyboard is a
 * block they cannot read, and this is precisely the content they are attesting to.
 */
function CanonicalJsonBlock({ canonicalJson, hash }: CanonicalJsonBlockProps): ReactElement {
  return (
    <SpaceBetween size="xs">
      <Header variant="h3" description="These exact bytes are what the SHA-256 below covers.">
        Canonical desired state
      </Header>
      <pre aria-label="Canonical desired state" tabIndex={0} style={CANONICAL_JSON_STYLE}>
        {canonicalJson}
      </pre>
      <div>
        <Box variant="awsui-key-label">Desired-state hash (SHA-256 of the bytes above)</Box>
        <code>{hash}</code>
      </div>
    </SpaceBetween>
  );
}

interface DesiredChangeDetailsProps {
  change: AeosDesiredChange;
}

function DesiredChangeDetails({ change }: DesiredChangeDetailsProps): ReactElement {
  const record: Record<string, unknown> = { ...change };
  const keys = Object.keys(record).sort((left, right) => left.localeCompare(right));
  return (
    <ColumnLayout columns={2} variant="text-grid">
      {keys.map((key) => (
        <div key={key}>
          <Box variant="awsui-key-label">{key}</Box>
          <Box>{formatChangeValue(record[key])}</Box>
        </div>
      ))}
    </ColumnLayout>
  );
}

function formatChangeValue(value: unknown): string {
  if (typeof value === 'string') {
    return value.length === 0 ? '(empty string)' : value;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (Array.isArray(value)) {
    const entries: readonly unknown[] = value;
    return entries.length === 0 ? '(empty list)' : entries.map(formatChangeValue).join(', ');
  }
  if (value === undefined || value === null) {
    return 'not set';
  }
  return JSON.stringify(value);
}

function approvalReasonText(reason: AeosApprovalReason): string {
  switch (reason) {
    case 'READY':
      return 'The server will accept your approval of this revision.';
    case 'NOT_PENDING':
      return 'The server refuses approval: this request is not awaiting approval.';
    case 'APPROVER_GROUP_REQUIRED':
      return 'The server refuses approval: membership of an AEOS approver group is required.';
    case 'SELF_APPROVAL_FORBIDDEN':
      return 'The server refuses approval: the requester cannot approve their own change request.';
  }
}

/* ------------------------------------------------------------------ */
/* Approval modal                                                      */
/* ------------------------------------------------------------------ */

interface ApprovalModalProps {
  review: AeosChangeReview | undefined;
  isVisible: boolean;
  isConfirmed: boolean;
  isSubmitting: boolean;
  onConfirm: (isConfirmed: boolean) => void;
  onDismiss: () => void;
  onApprove: () => Promise<void>;
}

function ApprovalModal(props: ApprovalModalProps): ReactElement {
  const { review } = props;
  return (
    <Modal
      visible={props.isVisible}
      onDismiss={props.onDismiss}
      header="Approve this exact desired state"
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            {review && !review.approval.allowed ? (
              <Box variant="small">{approvalReasonText(review.approval.reason)}</Box>
            ) : null}
            <Button variant="link" onClick={props.onDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={review === undefined || !review.approval.allowed || !props.isConfirmed}
              loading={props.isSubmitting}
              onClick={() => void props.onApprove()}
            >
              Record approval
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      {review ? (
        <SpaceBetween size="m">
          <Alert type="warning" header="This records intent only">
            {APPLIED_NOTICE}
          </Alert>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Expected lifecycle revision</Box>
              <Box>{review.lifecycleRevision}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Expected desired-state hash</Box>
              <code>{review.desiredStateHash}</code>
            </div>
          </ColumnLayout>
          <Box variant="p">
            Both expectations are submitted with the approval. If either has moved the
            server refuses and re-review is required.
          </Box>
          <CanonicalJsonBlock
            canonicalJson={review.canonicalJson}
            hash={review.desiredStateHash}
          />
          <Checkbox
            checked={props.isConfirmed}
            onChange={({ detail }) => props.onConfirm(detail.checked)}
          >
            I read the canonical bytes above and approve this exact desired state.
          </Checkbox>
        </SpaceBetween>
      ) : (
        <Box>No change request is selected.</Box>
      )}
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Create form                                                         */
/* ------------------------------------------------------------------ */

interface ChangeFormState {
  kind: AeosChangeKind;
  idempotencyKey: string;
  moduleId: string;
  isPaused: boolean;
  reason: string;
  overdueCriticalDays: string;
  unattendedGraceDays: string;
  materialGapCents: string;
  billablePhases: readonly AeosBillablePhase[];
  accountId: string;
  isReconciliationExempt: boolean;
  exemptReason: string;
  accountMaterialGapCents: string;
  accountUnattendedGraceDays: string;
  isCsManaged: boolean;
  prospectDocAccess: 'unset' | 'true' | 'false';
}

function newFormState(): ChangeFormState {
  return {
    kind: 'module-pause',
    idempotencyKey: crypto.randomUUID(),
    moduleId: '',
    isPaused: false,
    reason: '',
    overdueCriticalDays: '30',
    unattendedGraceDays: '7',
    materialGapCents: '5000',
    billablePhases: ['LAUNCHED'],
    accountId: '',
    isReconciliationExempt: false,
    exemptReason: '',
    accountMaterialGapCents: '',
    accountUnattendedGraceDays: '',
    isCsManaged: true,
    prospectDocAccess: 'unset',
  };
}

type UpdateForm = <K extends keyof ChangeFormState>(key: K, value: ChangeFormState[K]) => void;

interface ChangeFormModalProps {
  isVisible: boolean;
  isSubmitting: boolean;
  onDismiss: () => void;
  onSubmit: (input: AeosChangeRequestCreate) => Promise<void>;
}

function ChangeFormModal(props: ChangeFormModalProps): ReactElement {
  const [form, setForm] = useState<ChangeFormState>(newFormState);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (props.isVisible) {
      setForm(newFormState());
      setError(undefined);
    }
  }, [props.isVisible]);

  const update: UpdateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  async function submit(): Promise<void> {
    const built = buildCreateInput(form);
    if (!built.success) {
      setError(built.error);
      return;
    }
    setError(undefined);
    await props.onSubmit(built.data);
  }

  const isGlobalPause = form.kind === 'module-pause' && form.isPaused;

  return (
    <Modal
      visible={props.isVisible}
      onDismiss={props.onDismiss}
      header="Record an AEOS control-plane change request"
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={props.onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" loading={props.isSubmitting} onClick={() => void submit()}>
              Record pending approval
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error ? <Alert type="error">{error}</Alert> : null}
        <Alert type="info" header="This records intent only">
          {APPLIED_NOTICE} Validation in this form is a convenience: the API re-validates
          every field and its rules are the authoritative ones.
        </Alert>
        {isGlobalPause ? (
          <Alert type="warning" header="Pausing stops the module for EVERY customer account">
            AEOS keeps one settings row per module with no account dimension, and its gate
            fails closed. Approving this pause stops the module everywhere until someone
            resumes it. A reason is required and is recorded with the refusal AEOS logs.
          </Alert>
        ) : null}
        <FormField label="Change kind" description="Determines the blast radius of the request.">
          <Select
            selectedOption={kindOption(form.kind)}
            options={AEOS_CHANGE_KINDS.map(kindOption)}
            ariaLabel="Change kind"
            onChange={({ detail }) => {
              const next = AEOS_CHANGE_KINDS.find(
                (candidate) => candidate === detail.selectedOption.value,
              );
              if (next) {
                update('kind', next);
              }
            }}
          />
        </FormField>
        <ChangeKindFields form={form} update={update} />
        <FormField label="Idempotency key" description="Generated per form; replaying it returns the same request.">
          <Input value={form.idempotencyKey} readOnly ariaLabel="Idempotency key" />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}

function kindOption(kind: AeosChangeKind): SelectProps.Option {
  const scope = kind === 'module-pause' || kind === 'invoice-policy' ? 'global' : 'account';
  return { value: kind, label: `${kind} (${scope} scope)` };
}

interface ChangeKindFieldsProps {
  form: ChangeFormState;
  update: UpdateForm;
}

function ChangeKindFields({ form, update }: ChangeKindFieldsProps): ReactElement {
  if (form.kind === 'module-pause') {
    return <ModulePauseFields form={form} update={update} />;
  }
  if (form.kind === 'invoice-policy') {
    return <InvoicePolicyFields form={form} update={update} />;
  }
  if (form.kind === 'account-policy') {
    return <AccountPolicyFields form={form} update={update} />;
  }
  return <AccountManagedFields form={form} update={update} />;
}

function ModulePauseFields({ form, update }: ChangeKindFieldsProps): ReactElement {
  return (
    <SpaceBetween size="m">
      <FormField label="Module id" description="Lowercase and hyphenated, for example ticket-sync.">
        <Input
          value={form.moduleId}
          ariaLabel="Module id"
          onChange={({ detail }) => update('moduleId', detail.value)}
        />
      </FormField>
      <FormField label="Desired state">
        <RadioGroup
          value={form.isPaused ? 'paused' : 'running'}
          onChange={({ detail }) => update('isPaused', detail.value === 'paused')}
          items={[
            { value: 'running', label: 'Resume the module for every account' },
            { value: 'paused', label: 'Pause the module for every account' },
          ]}
        />
      </FormField>
      <FormField
        label={form.isPaused ? 'Reason (required when pausing)' : 'Reason (optional when resuming)'}
        description={`At most ${AEOS_REASON_MAX_LENGTH} characters. AEOS surfaces this beside the actor in the refusal it logs.`}
      >
        <Textarea
          value={form.reason}
          rows={3}
          ariaLabel="Reason"
          onChange={({ detail }) => update('reason', detail.value)}
        />
      </FormField>
    </SpaceBetween>
  );
}

function InvoicePolicyFields({ form, update }: ChangeKindFieldsProps): ReactElement {
  const bounds = AEOS_INVOICE_POLICY_BOUNDS;
  return (
    <SpaceBetween size="m">
      <ColumnLayout columns={3}>
        <FormField
          label="Overdue critical days"
          description={`${bounds.overdueCriticalDays.min}–${bounds.overdueCriticalDays.max}`}
        >
          <Input
            type="number"
            value={form.overdueCriticalDays}
            ariaLabel="Overdue critical days"
            onChange={({ detail }) => update('overdueCriticalDays', detail.value)}
          />
        </FormField>
        <FormField
          label="Unattended grace days"
          description={`${bounds.unattendedGraceDays.min}–${bounds.unattendedGraceDays.max}`}
        >
          <Input
            type="number"
            value={form.unattendedGraceDays}
            ariaLabel="Unattended grace days"
            onChange={({ detail }) => update('unattendedGraceDays', detail.value)}
          />
        </FormField>
        <FormField
          label="Material gap (cents)"
          description={`${bounds.materialGapCents.min}–${bounds.materialGapCents.max}`}
        >
          <Input
            type="number"
            value={form.materialGapCents}
            ariaLabel="Material gap in cents"
            onChange={({ detail }) => update('materialGapCents', detail.value)}
          />
        </FormField>
      </ColumnLayout>
      <FormField
        label="Billable phases"
        description="At least one. An empty billable set makes underbilling unfireable, which reads as permanent health."
      >
        <Multiselect
          selectedOptions={form.billablePhases.map(phaseOption)}
          options={AEOS_BILLABLE_PHASES.map(phaseOption)}
          ariaLabel="Billable phases"
          onChange={({ detail }) => update('billablePhases', selectedPhases(detail.selectedOptions))}
        />
      </FormField>
    </SpaceBetween>
  );
}

function phaseOption(phase: AeosBillablePhase): SelectProps.Option {
  return { value: phase, label: phase };
}

function selectedPhases(options: readonly SelectProps.Option[]): AeosBillablePhase[] {
  const values = new Set<string>();
  for (const option of options) {
    if ('value' in option && typeof option.value === 'string') {
      values.add(option.value);
    }
  }
  return AEOS_BILLABLE_PHASES.filter((phase) => values.has(phase));
}

function AccountPolicyFields({ form, update }: ChangeKindFieldsProps): ReactElement {
  return (
    <SpaceBetween size="m">
      <FormField label="Account id" description="The AEOS customer account this policy governs.">
        <Input
          value={form.accountId}
          ariaLabel="Account id"
          onChange={({ detail }) => update('accountId', detail.value)}
        />
      </FormField>
      <FormField label="Reconciliation">
        <RadioGroup
          value={form.isReconciliationExempt ? 'exempt' : 'reconciled'}
          onChange={({ detail }) =>
            update('isReconciliationExempt', detail.value === 'exempt')
          }
          items={[
            { value: 'reconciled', label: 'Reconcile this account' },
            { value: 'exempt', label: 'Exempt this account from reconciliation' },
          ]}
        />
      </FormField>
      <FormField
        label="Exemption reason"
        description="Required for an exemption and rejected otherwise: it persists as the standing justification."
      >
        <Textarea
          value={form.exemptReason}
          rows={3}
          ariaLabel="Exemption reason"
          onChange={({ detail }) => update('exemptReason', detail.value)}
        />
      </FormField>
      <ColumnLayout columns={2}>
        <FormField label="Material gap override (cents)" description="Leave blank to inherit the global policy. Blank is not zero.">
          <Input
            type="number"
            value={form.accountMaterialGapCents}
            ariaLabel="Account material gap override in cents"
            onChange={({ detail }) => update('accountMaterialGapCents', detail.value)}
          />
        </FormField>
        <FormField label="Unattended grace override (days)" description="Leave blank to inherit the global policy.">
          <Input
            type="number"
            value={form.accountUnattendedGraceDays}
            ariaLabel="Account unattended grace override in days"
            onChange={({ detail }) => update('accountUnattendedGraceDays', detail.value)}
          />
        </FormField>
      </ColumnLayout>
    </SpaceBetween>
  );
}

function AccountManagedFields({ form, update }: ChangeKindFieldsProps): ReactElement {
  return (
    <SpaceBetween size="m">
      <FormField label="Account id">
        <Input
          value={form.accountId}
          ariaLabel="Account id"
          onChange={({ detail }) => update('accountId', detail.value)}
        />
      </FormField>
      <FormField label="Customer success managed">
        <RadioGroup
          value={form.isCsManaged ? 'managed' : 'unmanaged'}
          onChange={({ detail }) => update('isCsManaged', detail.value === 'managed')}
          items={[
            { value: 'managed', label: 'CS managed' },
            { value: 'unmanaged', label: 'Not CS managed' },
          ]}
        />
      </FormField>
      <FormField label="Prospect document access">
        <RadioGroup
          value={form.prospectDocAccess}
          onChange={({ detail }) => update('prospectDocAccess', prospectAccessValue(detail.value))}
          items={[
            { value: 'unset', label: 'Leave unchanged (field omitted from the request)' },
            { value: 'true', label: 'Grant prospect document access' },
            { value: 'false', label: 'Deny prospect document access' },
          ]}
        />
      </FormField>
    </SpaceBetween>
  );
}

function prospectAccessValue(value: string): ChangeFormState['prospectDocAccess'] {
  if (value === 'true' || value === 'false') {
    return value;
  }
  return 'unset';
}

type BuildResult =
  | { success: true; data: AeosChangeRequestCreate }
  | { success: false; error: string };

/**
 * Builds and locally validates the request.
 *
 * The local parse gives immediate feedback on bounds the API also enforces. It is not
 * a gate: the API re-validates every field, and its answer is the one that counts.
 */
function buildCreateInput(form: ChangeFormState): BuildResult {
  const parsed = aeosChangeRequestCreateSchema.safeParse({
    idempotencyKey: form.idempotencyKey,
    change: buildChange(form),
  });
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    const path = issue?.path.join('.') ?? '';
    const message = issue?.message ?? 'The change request is invalid';
    return { success: false, error: path.length > 0 ? `${path}: ${message}` : message };
  }
  return { success: true, data: parsed.data };
}

function buildChange(form: ChangeFormState): unknown {
  if (form.kind === 'module-pause') {
    return {
      kind: 'module-pause',
      moduleId: form.moduleId.trim(),
      paused: form.isPaused,
      ...(form.reason.trim().length === 0 ? {} : { reason: form.reason.trim() }),
    };
  }
  if (form.kind === 'invoice-policy') {
    return {
      kind: 'invoice-policy',
      overdueCriticalDays: toNumber(form.overdueCriticalDays),
      unattendedGraceDays: toNumber(form.unattendedGraceDays),
      materialGapCents: toNumber(form.materialGapCents),
      billablePhases: [...form.billablePhases],
    };
  }
  if (form.kind === 'account-policy') {
    return {
      kind: 'account-policy',
      accountId: form.accountId.trim(),
      reconciliationExempt: form.isReconciliationExempt,
      ...(form.exemptReason.trim().length === 0 ? {} : { exemptReason: form.exemptReason.trim() }),
      ...optionalNumberField('materialGapCents', form.accountMaterialGapCents),
      ...optionalNumberField('unattendedGraceDays', form.accountUnattendedGraceDays),
    };
  }
  return {
    kind: 'account-managed',
    accountId: form.accountId.trim(),
    csManaged: form.isCsManaged,
    ...(form.prospectDocAccess === 'unset'
      ? {}
      : { prospectDocAccess: form.prospectDocAccess === 'true' }),
  };
}

/** NaN is passed through deliberately: the schema rejects it with a clear message. */
function toNumber(value: string): number {
  return Number(value.trim());
}

function optionalNumberField(key: string, value: string): Record<string, number> {
  return value.trim().length === 0 ? {} : { [key]: toNumber(value) };
}
